import { Injectable } from '@angular/core';
import { ApiConnection } from './apiConnection';
import { Message } from '../model/message';
import { Observable, Subject } from 'rxjs';
import { share } from 'rxjs/operators';
import { Work } from '../model/work';
import * as io from 'socket.io-client';

@Injectable()
export class SocketService {

  /** Configuring a global socket */

  private socket;

  /**
   * Lazy Subjects — one per socket event.
   *
   * Each subject is created on the first call to its `*Reciever()` method and
   * the underlying `socket.on()` listener is registered exactly once at that
   * moment. Subsequent callers share the same subject (via `share()`), so we no
   * longer leak a new socket.io listener per subscriber.
   *
   * Note: `share({ resetOnRefCountZero: false })` (RxJS 7+) is not available on
   * the pinned RxJS 6.6.7. Plain `share()` is correct here because the Subject
   * instance and the `socket.on` listener persist independently of share's
   * ref-count — only the multicast wiring is ref-counted.
   */
  private newMessageSubject: Subject<any> | null = null;
  private updateMessageSubject: Subject<any> | null = null;
  private deleteMessageSubject: Subject<any> | null = null;
  private getMessagesSubject: Subject<any> | null = null;
  private unseenMessagesSubject: Subject<any> | null = null;
  private attachmentListSubject: Subject<any> | null = null;
  private worksSubject: Subject<any> | null = null;
  private updateWorkSubject: Subject<any> | null = null;
  private updateWorkStatusSubject: Subject<any> | null = null;
  private syncMessageStatusSubject: Subject<any> | null = null;

  constructor() {
    let connected = false;
    const RETRY_INTERVAL = 10000;

    this.socket = io(ApiConnection.BASE_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      secure: true,
      // The server requires a valid JWT on the handshake (socket.io v2 has no
      // browser websocket headers, so the token goes in the query).
      query: { token: sessionStorage.getItem('CDFEE2BC43D63CAEAA3B169AD31E966C') || '' },
      reconnection: true,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    this.socket.on('connect', function () {
      connected = true;
    });
    this.socket.on('disconnect', function () {
      connected = false;
      retryConnectOnFailure(RETRY_INTERVAL);
    });

    const retryConnectOnFailure = function (retryInMilliseconds) {
      setTimeout(function () {
        if (!connected) {

          // http.get(`${ApiConnection.BASE_URL + ApiConnection.API_VERSION}ping`).subscribe(
          //     (data: any) => {
          //         connected = true;
          //         window.location.href = unescape(window.location.pathname);
          //     },
          //     (error: any) => {
          //         retryConnectOnFailure(retryInMilliseconds);
          //     }
          // );
        }
      }, retryInMilliseconds);
    };

    this.socket.on('connect_error', (error) => {
      // console.log(error);
    });

    this.socket.on('connect_timeout', (timeout) => {
      // console.log(timeout);
    });

    this.socket.on('error', (error) => {
      //  console.log(error);
    });

    this.socket.on('reconnect_attempt', () => {
      this.socket.io.opts.transports = ['polling', 'websocket'];
    });

    /** eof sockets */
  }

  /**
   * Join the socket to center and work rooms so scoped events reach
   * this client. Must be called after the connection is established
   * and the user's center data is available.
   */
  public joinCenter(centerId?: string, workId?: string): void {
    const selectedCenter = sessionStorage.getItem('50EE60E5468D8FC43984228303D24EE9');
    let centerGuid: string | undefined;
    if (selectedCenter) {
      try {
        centerGuid = JSON.parse(selectedCenter).guid;
      } catch (error) {
        centerGuid = undefined;
      }
    }
    this.socket.emit('join-center', { centerId, workId, centerGuid });
  }

  // Messages CRUD with Socket IO
  public async sendMessageEmitter(message: Message): Promise<void> {
    this.socket.emit('new-message', message);
  }

  public sendMessageReciever(): Observable<any> {
    if (!this.newMessageSubject) {
      const subject = new Subject<any>();
      this.socket.on('new-message', (message) => subject.next(message));
      this.newMessageSubject = subject;
    }
    return this.newMessageSubject.asObservable().pipe(share());
  }

  public async updateMessageEmitter(message: Message): Promise<void> {
    this.socket.emit('update-message', message);
  }

  public updateMessageReciever(): Observable<any> {
    if (!this.updateMessageSubject) {
      const subject = new Subject<any>();
      this.socket.on('update-message', (message) => subject.next(message));
      this.updateMessageSubject = subject;
    }
    return this.updateMessageSubject.asObservable().pipe(share());
  }

  public deleteMessageEmitter = (messageId: string) => {
    this.socket.emit('delete-message', messageId);
  }

  public deleteMessageReciever(): Observable<any> {
    if (!this.deleteMessageSubject) {
      const subject = new Subject<any>();
      this.socket.on('delete-message', (message) => subject.next(message));
      this.deleteMessageSubject = subject;
    }
    return this.deleteMessageSubject.asObservable().pipe(share());
  }

  public getMessagesEmitter = () => {
    this.socket.emit('get-messages');
  }

  public getMessagesReciever(): Observable<any> {
    if (!this.getMessagesSubject) {
      const subject = new Subject<any>();
      this.socket.on('get-messages', (messages) => subject.next(messages));
      this.getMessagesSubject = subject;
    }
    return this.getMessagesSubject.asObservable().pipe(share());
  }

  public getUnseenMessagesEmitter = () => {
    this.socket.emit('unseen-message-list');
  }

  public getUnseenMessagesReciever(): Observable<any> {
    if (!this.unseenMessagesSubject) {
      const subject = new Subject<any>();
      this.socket.on('unseen-message-list', (messages) => subject.next(messages));
      this.unseenMessagesSubject = subject;
    }
    return this.unseenMessagesSubject.asObservable().pipe(share());
  }

  public getAttachmentsByWorkIdEmitter = (workId: string) => {
    this.socket.emit('get-attachment-list', workId);
  }

  public getAttachmentsByWorkIdReciever(): Observable<any> {
    if (!this.attachmentListSubject) {
      const subject = new Subject<any>();
      this.socket.on('get-attachment-list', (attachments) => subject.next(attachments));
      this.attachmentListSubject = subject;
    }
    return this.attachmentListSubject.asObservable().pipe(share());
  }

  // Works CRUD with Socket IO

  public getWorksEmitter = (id: string, type: string) => {
    this.socket.emit('get-works', id, type);
  }

  public getWorksReciever(): Observable<any> {
    if (!this.worksSubject) {
      const subject = new Subject<any>();
      this.socket.on('get-works', (works) => subject.next(works));
      this.worksSubject = subject;
    }
    return this.worksSubject.asObservable().pipe(share());
  }

  public async updateWorkEmitter(work: Work): Promise<void> {
    this.socket.emit('update-work', work);
  }

  public updateWorkReciever(): Observable<any> {
    if (!this.updateWorkSubject) {
      const subject = new Subject<any>();
      this.socket.on('update-work', (work) => subject.next(work));
      this.updateWorkSubject = subject;
    }
    return this.updateWorkSubject.asObservable().pipe(share());
  }

  public updateWorkStatusReciever(): Observable<any> {
    if (!this.updateWorkStatusSubject) {
      const subject = new Subject<any>();
      this.socket.on('update-work-status', (work) => subject.next(work));
      this.updateWorkStatusSubject = subject;
    }
    return this.updateWorkStatusSubject.asObservable().pipe(share());
  }

  public syncMessageStatusEmitter = (workId?: string) => {
    this.socket.emit('sync-message-status', workId);
  }

  public syncMessageStatusReceiver(): Observable<any> {
    if (!this.syncMessageStatusSubject) {
      const subject = new Subject<any>();
      this.socket.on('sync-message-status', (workId) => subject.next(workId));
      this.syncMessageStatusSubject = subject;
    }
    return this.syncMessageStatusSubject.asObservable().pipe(share());
  }

  /**
   * Tears down all socket listeners and completes every lazy Subject.
   *
   * Call this when the service is no longer needed (e.g. on application
   * teardown) to release socket.io resources and complete subscribers. The
   * global Angular service is typically alive for the whole session, so this is
   * primarily a safety net for hot-reload / explicit teardown scenarios.
   */
  public destroy(): void {
    this.socket.removeAllListeners();
    this.newMessageSubject?.complete();
    this.updateMessageSubject?.complete();
    this.deleteMessageSubject?.complete();
    this.getMessagesSubject?.complete();
    this.unseenMessagesSubject?.complete();
    this.attachmentListSubject?.complete();
    this.worksSubject?.complete();
    this.updateWorkSubject?.complete();
    this.updateWorkStatusSubject?.complete();
    this.syncMessageStatusSubject?.complete();
  }

}
